import { useCallback, useEffect, useMemo, useState } from 'react';
import { batchesAPI } from '../services/api';
import usePermissions from './usePermissions';
import {
  assignmentTrainerId,
  parseTrainerListResponse,
} from '../utils/batchTrainerDisplay';

const ASSIGN_PERMISSION = 'batches.assign_trainers';

/**
 * Encapsulates batch-trainer link/unlink state and API calls (SRP for UI).
 */
export default function useBatchTrainers(batchId, initialTrainers = [], onChanged) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(ASSIGN_PERMISSION);

  const [assignments, setAssignments] = useState(initialTrainers || []);
  const [options, setOptions] = useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setAssignments(initialTrainers || []);
  }, [initialTrainers]);

  const linkedIds = useMemo(
    () => new Set(assignments.map(assignmentTrainerId).filter(Boolean)),
    [assignments]
  );

  const availableOptions = useMemo(
    () => options.filter((t) => !linkedIds.has(t.id)),
    [options, linkedIds]
  );

  const loadOptions = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await batchesAPI.getTrainerOptions();
      setOptions(parseTrainerListResponse(res));
    } catch (err) {
      console.error('Failed to load trainers', err);
      setError(err?.response?.data?.message || 'Could not load trainer list');
    }
  }, [canManage]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const refresh = useCallback(async () => {
    const res = await batchesAPI.getTrainers(batchId);
    const list = parseTrainerListResponse(res);
    setAssignments(list);
    onChanged?.(list);
    return list;
  }, [batchId, onChanged]);

  const linkTrainer = useCallback(
    async (trainerId) => {
      if (!trainerId) return false;
      setLoading(true);
      setError('');
      try {
        await batchesAPI.linkTrainer(batchId, trainerId);
        setSelectedTrainerId('');
        await refresh();
        await loadOptions();
        return true;
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not link trainer');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [batchId, refresh, loadOptions]
  );

  const unlinkTrainer = useCallback(
    async (trainerId) => {
      if (!trainerId) return false;
      if (!window.confirm('Unlink this trainer from the batch?')) return false;
      setBusyId(trainerId);
      setError('');
      try {
        await batchesAPI.unlinkTrainer(batchId, trainerId);
        await refresh();
        await loadOptions();
        return true;
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not unlink trainer');
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [batchId, refresh, loadOptions]
  );

  return {
    canManage,
    assignments,
    availableOptions,
    selectedTrainerId,
    setSelectedTrainerId,
    loading,
    error,
    busyId,
    linkTrainer,
    unlinkTrainer,
  };
}
